import { ResourceType } from "@/game/entities/config/resources";
import GameError, { GameErrorType } from "@/libs/GameError";
import {
	createImmutableState,
	getImmutableStateProperty,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { type ComputedRef } from "vue";

type ResourcesStorageStoreState = Record<ResourceType, number>;

type ResourcesStorageStoreGetters = Record<ResourceType, ComputedRef<number>>;

type ResourcesStorageStore = ResourcesStorageStoreGetters & {
	set: (type: ResourceType, value: number) => void;
	increase: (type: ResourceType, value: number) => void;
	decrease: (type: ResourceType, value: number) => void;
};

const STORE_NAME = "storage/resources";

export default defineStore(STORE_NAME, (): ResourcesStorageStore => {
	const keys = (Object.values(ResourceType) as Array<ResourceType>).filter(
		(key: ResourceType): boolean =>
			[
				ResourceType.LUMBER,
				ResourceType.STONE,
				ResourceType.CLAY,
				ResourceType.IRON,
				ResourceType.GOODS,
				ResourceType.STEEL,
				ResourceType.ARMY,
				ResourceType.RATIONS,
			].includes(key),
	);

	const state = createImmutableState<ResourcesStorageStoreState>(
		keys.reduce(
			(
				acc: ResourcesStorageStoreState,
				key: ResourceType,
			): ResourcesStorageStoreState => ({ ...acc, [key]: 0 }),
			{} as ResourcesStorageStoreState,
		),
	);

	const getters = keys.reduce(
		(
			acc: ResourcesStorageStoreGetters,
			key: ResourceType,
		): ResourcesStorageStoreGetters => ({
			...acc,
			[key]: getImmutableStateProperty(state, key, 0),
		}),
		{} as ResourcesStorageStoreGetters,
	);

	const set = (key: ResourceType, value: number): void => {
		if (isNaN(value))
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid value for resource ${key}: ${value}`,
			);

		setImmutableStateProperty(state, key, value);
	};

	const increase = (key: ResourceType, value: number): void => {
		if (isNaN(value))
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid value for resource ${key}: ${value}`,
			);

		setImmutableStateProperty(state, key, (state.value[key] ?? 0) + value);
	};

	const decrease = (key: ResourceType, value: number): void => {
		if (isNaN(value))
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid value for resource ${key}: ${value}`,
			);

		setImmutableStateProperty(state, key, (state.value[key] ?? 0) - value);
	};

	return { ...getters, set, increase, decrease };
});
